import { ecrireJson, lireJson, type Resultat } from './client';
import type { Intervention } from './interventions';

/** Situations d'un client — liste fermée, identique à celle du serveur. */
export type SituationClient = 'en-cours' | 'planifiee' | 'sans-suite';

/** Tris possibles — liste fermée, identique à celle du serveur. */
export type TriClients = 'nom' | 'interventions' | 'activite';

/** Sens du tri. */
export type SensTri = 'asc' | 'desc';

/**
 * Une ligne de la liste des clients.
 *
 * `situation`, `interventions`, `aVenir`, `dernierPassage`, `derniereActivite` et
 * `activiteLibelle` sont CALCULÉS par le serveur à partir des interventions :
 * l'écran ne les recompte jamais et ne formule aucune date relative.
 */
export interface ClientResume {
  readonly id: string;
  readonly nom: string;
  readonly contact: string;
  readonly email: string;
  readonly telephone: string;
  readonly ville: string;
  readonly siret: string | null;
  readonly secteur: string | null;
  readonly fonction: string | null;
  readonly rue: string | null;
  readonly codePostal: string | null;
  readonly notes: string | null;
  readonly situation: SituationClient;
  readonly interventions: number;
  readonly aVenir: number;
  readonly dernierPassage: string | null;
  readonly derniereActivite: string | null;
  readonly activiteLibelle: string | null;
}

/** Les valeurs proposées par les filtres, calculées sur le portefeuille réel. */
export interface ChoixFiltres {
  readonly villes: readonly string[];
  readonly secteurs: readonly string[];
}

export interface PageClients {
  readonly items: readonly ClientResume[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
  readonly pages: number;
  readonly filtres?: ChoixFiltres;
  /** Toutes les valeurs admises pour le secteur (liste fermée), pour la saisie. */
  readonly secteursPossibles?: readonly string[];
  readonly tris?: ReadonlyArray<{ readonly cle: TriClients; readonly libelle: string }>;
  readonly tri?: { readonly cle: TriClients; readonly sens: SensTri };
  readonly aujourdHui?: string;
}

/** La fiche d'un client : ses coordonnées et SES interventions. */
export interface FicheClient {
  readonly client: ClientResume;
  readonly interventions: readonly Intervention[];
}

/** Ce que l'écran envoie pour créer un client (les identifiants viennent du serveur). */
export interface SaisieClient {
  readonly nom: string;
  readonly contact: string;
  readonly email: string;
  readonly telephone: string;
  readonly ville: string;
  readonly siret?: string;
  readonly secteur?: string;
  readonly fonction?: string;
  readonly rue?: string;
  readonly codePostal?: string;
  readonly notes?: string;
}

/** Modification partielle : un champ absent n'est pas touché, un champ vide efface. */
export type ChangementClient = Partial<SaisieClient>;

export interface Criteres {
  readonly recherche?: string;
  readonly situation?: SituationClient | '';
  readonly ville?: string;
  readonly secteur?: string;
  readonly tri?: TriClients;
  readonly sens?: SensTri;
  readonly page?: number;
  readonly taille?: number;
}

/**
 * Construit l'URL de la liste. Le tri, le comptage et la pagination sont faits par
 * le serveur : l'écran n'envoie que des critères.
 */
export function urlClients(criteres: Criteres = {}): string {
  const parametres = new URLSearchParams();
  const recherche = criteres.recherche?.trim();
  if (recherche) parametres.set('recherche', recherche);
  if (criteres.situation) parametres.set('situation', criteres.situation);
  if (criteres.ville) parametres.set('ville', criteres.ville);
  if (criteres.secteur) parametres.set('secteur', criteres.secteur);
  if (criteres.tri && criteres.tri !== 'nom') parametres.set('tri', criteres.tri);
  if (criteres.sens === 'desc') parametres.set('sens', 'desc');
  if (criteres.page && criteres.page > 1) parametres.set('page', String(criteres.page));
  if (criteres.taille) parametres.set('taille', String(criteres.taille));

  const requete = parametres.toString();
  return requete === '' ? '/api/clients' : `/api/clients?${requete}`;
}

/** L'export suit les mêmes filtres que la liste, sans la pagination. */
export function urlExportClients(criteres: Criteres = {}): string {
  const url = urlClients({ ...criteres, page: undefined, taille: undefined });
  return url === '/api/clients'
    ? '/api/clients/export.csv'
    : `/api/clients/export.csv${url.slice(url.indexOf('?'))}`;
}

export function listerClients(
  criteres: Criteres = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageClients>> {
  return lireJson<PageClients>(urlClients(criteres), options);
}

/**
 * Fiche d'UN client, demandée au serveur.
 *
 * L'écran ne recopie pas la ligne de la liste : le jour où la liste renverra des
 * colonnes choisies, la fiche doit encore être complète — et ses interventions
 * sont celles que le serveur décide, pas celles qui traînent à l'écran.
 */
export function lireClient(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<FicheClient>> {
  return lireJson<FicheClient>(`/api/clients/${encodeURIComponent(id)}`, options);
}

/** Crée un client dans le portefeuille du compte connecté. */
export function creerClient(
  saisie: SaisieClient,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ClientResume>> {
  return ecrireJson<ClientResume>('/api/clients', 'POST', saisie, options);
}

/** Modifie un client. Le serveur refuse (404) celui d'un autre compte. */
export function modifierClient(
  id: string,
  changement: ChangementClient,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ClientResume>> {
  return ecrireJson<ClientResume>(
    `/api/clients/${encodeURIComponent(id)}`,
    'PATCH',
    changement,
    options,
  );
}

/**
 * Archive des clients (ils quittent le portefeuille actif, leurs interventions
 * restent au registre). Le compte des lignes vient du serveur.
 */
export function archiverClients(
  ids: readonly string[],
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<{ archives: number }>> {
  return ecrireJson<{ archives: number }>('/api/clients/archivage', 'POST', { ids }, options);
}
